<template>
  <div>
    <validation-observer ref="loginValidation">
      <b-form class="auth-login-form mt-2" @submit.prevent>
        <!-- email -->
        <b-row>
          <b-col>
            <b-form-group label="Email" label-for="login-email">
              <validation-provider #default="{ errors }" name="Email" rules="required|email">
                <b-form-input
                  id="login-email"
                  v-model="userEmail"
                  :state="errors.length > 0 ? false : null"
                  name="login-email"
                  placeholder="seu.email@empresa.com"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- senha -->
        <b-row>
          <b-col>
            <b-form-group label="Senha" label-for="login-password">
              <validation-provider #default="{ errors }" name="Senha" rules="required">
                <b-input-group class="input-group-merge" :class="errors.length ? 'is-invalid' : null">
                  <b-form-input
                    id="login-password"
                    v-model="password"
                    :state="errors.length > 0 ? false : null"
                    class="form-control-merge"
                    :type="passwordFieldType"
                    name="login-password"
                    placeholder="············"
                    autocomplete="current-password"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="passwordToggleIcon"
                      @click="togglePasswordVisibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- utils -->
        <div class="d-flex align-items-center justify-content-between mb-1">
          <b-form-checkbox id="remember-me" v-model="status" name="remember">
            Lembrar
          </b-form-checkbox>

          <b-link :to="{ name: 'perdi-minha-senha' }">
            <small>Esqueci a senha</small>
          </b-link>
        </div>

        <!-- actions -->
        <b-button
          type="submit"
          variant="primary"
          block
          :disabled="isSubmittingLogin"
          @click="validationForm"
        >
          <span v-if="!isSubmittingLogin">Entrar</span>
          <span v-else>Entrando...</span>
        </b-button>

        <!-- Mensagem de erro inline do login -->
        <b-alert
          v-if="loginErrorMessage"
          show
          variant="danger"
          class="mt-1 p-2"
          aria-live="polite"
        >
          <div class="d-flex align-items-start">
            <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50 mt-25 text-danger" />
            <div class="flex-fill">
              {{ loginErrorMessage }}
            </div>
          </div>
        </b-alert>

        <div v-if="showWebAuthn" class="or-separator my-2">
          <span>ou</span>
        </div>

        <b-button
          v-if="showWebAuthn"
          type="button"
          variant="outline-secondary"
          block
          class="mt-1"
          @click="webAuthnLogin"
        >
          <feather-icon icon="ShieldIcon" class="mr-50" />
          Entrar com chave de acesso
        </b-button>
      </b-form>
    </validation-observer>

    <!-- Overlay de OTP (tela cheia, com blur no fundo) -->
    <div v-if="showOtpOverlay" class="otp-overlay d-flex align-items-center justify-content-center">
      <div class="otp-modal card glassy shadow-xl">
        <div class="card-body p-2 p-sm-3 p-md-4">
          <div class="d-flex align-items-center mb-2">
            <div class="otp-icon mr-2 d-flex align-items-center justify-content-center">
              <feather-icon icon="ShieldIcon" size="20" />
            </div>
            <div>
              <h4 class="mb-0 otp-title">
                Verificação em duas etapas
              </h4>
              <small class="text-muted otp-subtitle">
                Digite o código de {{ mfaMeta.code_length }} dígitos do seu app autenticador.
              </small>
            </div>
          </div>

          <!-- Erro inline do OTP -->
          <b-alert
            v-if="otpErrorMessage"
            show
            variant="danger"
            class="mb-2 p-2"
            aria-live="polite"
          >
            <div class="d-flex align-items-start">
              <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50 mt-25 text-danger" />
              <div class="flex-fill">
                {{ otpErrorMessage }}
              </div>
            </div>
          </b-alert>

          <!-- Inputs do OTP em caixas separadas -->
          <div class="otp-inputs my-3">
            <b-form-input
              v-for="(d, idx) in mfaMeta.code_length"
              :key="'otp-'+idx"
              ref="otpInput"
              v-model="otpDigits[idx]"
              type="tel"
              inputmode="numeric"
              autocomplete="one-time-code"
              class="otp-input"
              maxlength="1"
              :aria-label="`Dígito ${idx+1} de ${mfaMeta.code_length}`"
              @keydown.native="handleOtpKeydown(idx, $event)"
              @paste.native="onOtpPaste(idx, $event)"
              @input="onOtpDigitInput(idx)"
            />
          </div>

          <div class="d-flex align-items-center mb-2">
            <b-button
              variant="primary"
              class="flex-fill"
              :disabled="!canSubmitOtp || isSubmittingOtp"
              @click="submitOtp"
            >
              <span v-if="!isSubmittingOtp">Confirmar</span>
              <span v-else>Validando...</span>
            </b-button>
            <b-button
              variant="outline-secondary"
              class="ml-1"
              :disabled="isSubmittingOtp"
              @click="cancelOtp"
            >
              Cancelar
            </b-button>
          </div>

          <div class="d-flex align-items-center justify-content-between">
            <small v-if="mfaMeta && mfaMeta.period" class="text-muted">
              Código renova a cada {{ mfaMeta.period }}s.
            </small>
            <small class="text-muted">
              <feather-icon icon="InfoIcon" size="14" class="mr-25" />
              Você pode colar o código inteiro.
            </small>
          </div>
        </div>
      </div>
    </div>
    <!-- /Overlay de OTP -->
  </div>

</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from "vee-validate";
import {
  BRow,
  BCol,
  BLink,
  BFormGroup,
  BFormInput,
  BInputGroupAppend,
  BInputGroup,
  BFormCheckbox,
  BForm,
  BButton,
  BAlert
} from "bootstrap-vue";
import { required, email } from "@validations";
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import store from "@/store/index";
import { mapActions } from "vuex";
import { v4 as uuidv4 } from "uuid";

export default {
  name: "LoginForm",
  props: {
    // Quando usado dentro do modal de reautenticação, não deve redirecionar para home
    suppressRedirect: { type: Boolean, default: false }
  },
  emits: ["success"],
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BFormCheckbox,
    BForm,
    BButton,
    BAlert,
    ValidationProvider,
    ValidationObserver
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      showWebAuthn: false,
      status: "",
      password: "",
      userEmail: "",
      // validation rules
      required,
      email,

      // MFA/OTP
      showOtpOverlay: false,
      otpDigits: [],
      mfaMeta: { code_length: 6, period: 30 },
      pendingLoginPayload: null,
      isSubmittingLogin: false,
      isSubmittingOtp: false,

      // Inline error messages (em vez de $notify)
      loginErrorMessage: "",
      otpErrorMessage: ""
    };
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
    otpValue() {
      return (this.otpDigits || []).join("");
    },
    canSubmitOtp() {
      const len = this.mfaMeta && this.mfaMeta.code_length ? this.mfaMeta.code_length : 6;
      return typeof this.otpValue === "string"
        && this.otpValue.length === len
        && this.otpDigits.every(d => /^\d$/.test(d));
    }
  },
  mounted() {
    this.showWebAuthn = Webpass.isSupported();
  },
  methods: {
    ...mapActions("auth", ["me"]),

    // Auxiliares para mensagens de bloqueio (HTTP 423)
    formatRetryAfter(secs) {
      const s = Number(secs || 0)
      if (!s || s <= 0) return null
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      const parts = []
      if (h > 0) parts.push(`${h}h`)
      if (m > 0) parts.push(`${m}min`)
      if (!parts.length) parts.push(`${s}s`)
      return parts.join(' ')
    },
    buildLockMessage(data) {
      const retryText = this.formatRetryAfter(data && data.retry_after)
      let msg = 'Muitas tentativas. Sua conta foi temporariamente bloqueada.'
      if (retryText) msg += ` Tente novamente em ${retryText}.`
      return msg
    },

    // Redireciona após login apenas quando não estiver em modal
    async navigateAfterLogin() {
      if (this.suppressRedirect) return
      const next = (this.$route && this.$route.query && this.$route.query.next) ? this.$route.query.next : null
      const isSafeRelativePath = next && /^\/[\w\-./%?=&#+]*$/.test(next)
      if (isSafeRelativePath) {
        await this.$router.replace(next)
      } else {
        await this.$router.push({ name: 'home' })
      }
    },

    autoSubmitIfComplete() {
      if (this.canSubmitOtp && !this.isSubmittingOtp) {
        if (this._otpAutoTimer) clearTimeout(this._otpAutoTimer);
        this._otpAutoTimer = setTimeout(() => {
          if (this.canSubmitOtp && !this.isSubmittingOtp) this.submitOtp();
        }, 50);
      }
    },

    openOtpOverlay(meta = {}) {
      this.mfaMeta = {
        code_length: meta.code_length || 6,
        period: meta.period || 30
      };
      this.otpDigits = Array(this.mfaMeta.code_length).fill("");
      this.showOtpOverlay = true;
      this.otpErrorMessage = "";
      this.focusOtp(0);
    },

    cancelOtp() {
      this.showOtpOverlay = false;
      this.otpDigits = [];
      this.pendingLoginPayload = null;
    },

    focusOtp(index) {
      this.$nextTick(() => {
        const refList = this.$refs.otpInput;
        const input = Array.isArray(refList) ? refList[index] : refList;
        if (input && typeof input.focus === "function") input.focus();
      });
    },

    handleOtpKeydown(index, e) {
      const key = e.key;
      const isNumber = /^[0-9]$/.test(key);

      if ((e.ctrlKey || e.metaKey) && (key === "v" || key === "V")) return;
      if (e.shiftKey && key === "Insert") return;
      if (isNumber) return;

      if (key === "Backspace") {
        e.preventDefault();
        if (this.otpDigits[index]) {
          this.$set(this.otpDigits, index, "");
          return;
        }
        if (index > 0) {
          this.$set(this.otpDigits, index - 1, "");
          this.focusOtp(index - 1);
        }
        return;
      }

      if (key === "ArrowLeft" && index > 0) {
        e.preventDefault();
        this.focusOtp(index - 1);
        return;
      }
      if (key === "ArrowRight" && index < this.mfaMeta.code_length - 1) {
        e.preventDefault();
        this.focusOtp(index + 1);
        return;
      }

      if (key === "Enter") {
        e.preventDefault();
        if (this.canSubmitOtp) this.submitOtp();
        return;
      }

      if (!["Tab", "Delete"].includes(key)) e.preventDefault();
    },

    onOtpDigitInput(index) {
      const val = String(this.otpDigits[index] || "");
      const digit = (val.match(/\d/g) || []).slice(-1)[0] || "";
      this.$set(this.otpDigits, index, digit);
      if (digit && index < this.mfaMeta.code_length - 1) this.focusOtp(index + 1);
      this.autoSubmitIfComplete();
    },

    onOtpPaste(index, e) {
      const text = (e.clipboardData || window.clipboardData).getData("text");
      if (!text) return;
      const digits = (text.match(/\d/g) || []);
      if (!digits.length) return;
      e.preventDefault();

      const arr = [...this.otpDigits];
      let i = index;
      digits.forEach(d => {
        if (i < this.mfaMeta.code_length) {
          arr[i] = d;
          i += 1;
        }
      });
      this.otpDigits = arr;
      const nextEmpty = this.otpDigits.findIndex(v => v === "");
      if (nextEmpty === -1) this.focusOtp(this.mfaMeta.code_length - 1);
      else this.focusOtp(nextEmpty);
      this.autoSubmitIfComplete();
    },

    async validationForm() {
      this.$refs.loginValidation.validate().then(async success => {
        if (!success) return true;

        const payload = {
          username: this.userEmail,
          password: this.password,
          client_id: process.env.VUE_APP_CLIENT_ID,
          client_secret: process.env.VUE_APP_CLIENT_SECRET,
          grant_type: "password"
        };

        this.isSubmittingLogin = true;
        this.loginErrorMessage = "";
        try {
          const authUser = await this.$http.post("oauth/token", payload);

          if (authUser && authUser.error) {
            // Conta bloqueada (HTTP 423)
            if (Number(authUser.error) === 423 || authUser?.data?.error === 'too_many_failures') {
              this.loginErrorMessage = this.buildLockMessage(authUser.data || {})
              return false
            }
            if (authUser.id === "mfa_required" || (authUser.errors && authUser.errors.otp_required)) {
              this.pendingLoginPayload = payload;
              this.openOtpOverlay(authUser.errors || {});
              return false;
            }
            this.loginErrorMessage = "Usuário ou senha inválidos";
            return false;
          }

          localStorage.setItem("accessToken", authUser.access_token);
          localStorage.setItem("refreshToken", authUser.refresh_token);
          localStorage.setItem("dataToken", JSON.stringify(authUser));
          const me = await this.me();
          localStorage.setItem("userData", JSON.stringify(me));
          this.$emit('success')
          await this.navigateAfterLogin()
        } catch (e) {
          const data = (e && e.response && e.response.data) ? e.response.data : null;
          // Bloqueio 423 vindo por exceção
          if (e && e.response && e.response.status === 423) {
            this.loginErrorMessage = this.buildLockMessage(data || {})
            return false
          }
          if (data && (data.id === "mfa_required" || (data.errors && data.errors.otp_required))) {
            this.pendingLoginPayload = payload;
            this.openOtpOverlay(data.errors || {});
            return false;
          }
          this.loginErrorMessage = "Falha ao autenticar. Verifique suas credenciais.";
          return false;
        } finally {
          this.isSubmittingLogin = false;
        }
        return true;
      });
    },

    async submitOtp() {
      if (!this.canSubmitOtp) {
        this.otpErrorMessage = "Informe o código completo.";
        return;
      }
      if (!this.pendingLoginPayload) {
        this.otpErrorMessage = "Sessão de login expirada. Tente novamente.";
        this.cancelOtp();
        return;
      }

      this.isSubmittingOtp = true;
      try {
        const resp = await this.$http.post("oauth/token", {
          ...this.pendingLoginPayload,
          otp: String(this.otpValue)
        });

        if (resp && resp.error) {
          // Conta bloqueada (HTTP 423)
          if (Number(resp.error) === 423 || resp?.data?.error === 'too_many_failures') {
            this.otpErrorMessage = this.buildLockMessage(resp.data || {})
            return
          }
          if (resp.id === "mfa_required" || (resp.errors && resp.errors.otp_required)) {
            this.otpErrorMessage = "Código informado é inválido.";
            this.otpDigits = Array(this.mfaMeta.code_length).fill("");
            this.focusOtp(0);
            return;
          }
          this.otpErrorMessage = "Não foi possível autenticar.";
          return;
        }

        this.showOtpOverlay = false;
        localStorage.setItem("accessToken", resp.access_token);
        localStorage.setItem("refreshToken", resp.refresh_token);
        localStorage.setItem("dataToken", JSON.stringify(resp));
        const me = await this.me();
        localStorage.setItem("userData", JSON.stringify(me));
        this.$emit('success')
        await this.navigateAfterLogin()
      } catch (e) {
        const data = (e && e.response && e.response.data) ? e.response.data : null;
        if (e && e.response && e.response.status === 423) {
          this.otpErrorMessage = this.buildLockMessage(data || {})
        } else if (data && (data.id === "mfa_required" || (data.errors && data.errors.otp_required))) {
          this.otpErrorMessage = "Código invalido";
        } else {
          this.otpErrorMessage = "Falha ao validar o OTP. Tente novamente.";
        }
      } finally {
        this.isSubmittingOtp = false;
      }
    },

    async webAuthnLogin() {
      const webpass = Webpass.create({
        method: "post",
        baseURL: process.env.VUE_APP_URL_API,
        findCsrfToken: false,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "x-request-id": uuidv4()
        },
        credentials: "include"
      });
      if (Webpass.isUnsupported()) {
        this.loginErrorMessage = "Seu navegador não suporta WebAuthn.";
        return;
      }
      const { user, success, error } = await webpass.assert("/webauthn/login/options", "/webauthn/login");

      if (error) {
        this.loginErrorMessage = "Token inválido.";
        return false;
      }

      localStorage.setItem("accessToken", user.access_token);
      localStorage.setItem("refreshToken", user.refresh_token);
      localStorage.setItem("dataToken", JSON.stringify(user));
      const me = await this.me();
      localStorage.setItem("userData", JSON.stringify(me));
      this.$emit('success')
      await this.navigateAfterLogin()
    }
  }
};
</script>

<style lang="scss" scoped>
/* Overlay de OTP (tela cheia, com blur) */
.otp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(8, 10, 20, .55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 1050;
  padding: 1rem;
}

.otp-modal.glassy {
  width: 100%;
  max-width: 460px;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(108, 117, 125, 0.2);
  background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0.90) 100%
  );
}

.otp-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #7367F0 0%, #9E95F5 100%);
  color: #fff;
  flex: 0 0 auto;
  box-shadow: 0 6px 18px rgba(115, 103, 240, .35);
}

.otp-title {
  font-weight: 700;
}

.otp-subtitle {
  display: block;
}

.otp-inputs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(48px, 1fr));
  grid-auto-flow: column;
  justify-content: center;
  gap: 10px;
}

.otp-input {
  height: 56px;
  width: 56px;
  max-width: 64px;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: 2px;
  border-radius: 10px;
  border: 1px solid #d8d6de;
  transition: all .15s ease;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .04);

  &:focus {
    border-color: #7367F0;
    box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.15);
  }
}

/* Dark */
body.dark-layout {
  .otp-modal.glassy {
    background: linear-gradient(
        180deg,
        rgba(40, 48, 70, 0.88) 0%,
        rgba(40, 48, 70, 0.94) 100%
    );
    border-color: rgba(255, 255, 255, 0.08);
    color: #d0d2d6;
  }

  .otp-input {
    background: #1F2432;
    color: #EAEAEA;
    border-color: #3B4253;
  }
}

.or-separator {
  display: flex;
  align-items: center;
  color: #6e6b7b;
  font-size: 0.85rem;
  user-select: none;

  span {
    padding: 0 0.75rem;
  }

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-top: 1px solid #e9ecef;
  }
}
</style>
